import { useEffect, useState } from "react";
import { fetchAccess } from "../api.js";
import { SystemPage, SystemSection } from "./SystemPage.js";
import type { AccessOverview } from "../../shared/types.js";

/**
 * `/_access` — a read-only overview of the access model for admins: the config
 * groups + their members, the static level/op reference, and every repo's
 * committed `.datawiki/access` rules. View only — membership is edited in
 * `datawiki-config/groups/`, rules are edited as pages (docs/auth/AccessControl.md).
 */
export function AccessView() {
  const [data, setData] = useState<AccessOverview | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetchAccess()
      .then(setData)
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, []);

  if (err)
    return (
      <SystemPage title="Access">
        <p className="dw-error">{err}</p>
      </SystemPage>
    );
  if (!data) return <SystemPage title="Access">Loading…</SystemPage>;

  return (
    <SystemPage
      title="Access"
      lead="Read-only. Groups live in datawiki-config/groups/; per-path rules are .datawiki/access files, edited as pages."
    >
      <SystemSection title="Groups">
        <p className="dw-muted">
          Subjects you can name in a <code>.datawiki/access</code> file. The three
          built-ins always exist; named groups are directories under{" "}
          <code>datawiki-config/groups/</code>.
        </p>
        <GroupRow name="public" desc="anyone, incl. signed-out visitors — grant it read in datawiki-config/access to allow anonymous reads" />
        <GroupRow name="authed" desc="any signed-in user" />
        <GroupRow name="admins" desc="full everywhere, including every access file" logins={data.admins} />
        {data.groups.map((g) => (
          <GroupRow key={g.name} name={g.name} logins={g.logins} />
        ))}
      </SystemSection>

      <SystemSection title="Levels → operations">
        <table className="dw-access-table">
          <tbody>
            {data.levels.map((lvl) => (
              <tr key={lvl}>
                <td>
                  <code>{lvl}</code>
                </td>
                <td>
                  {data.levelOps[lvl].length
                    ? data.levelOps[lvl].map((op) => (
                        <code key={op} className="dw-access-op">
                          {op}
                        </code>
                      ))
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </SystemSection>

      <SystemSection title="Repo access">
        {data.repos.map((repo) => (
          <div key={repo.name} className="dw-access-repo">
            <h3>
              <code>{repo.name}</code>
              {repo.local && <span className="dw-muted"> (local — owner has full)</span>}
            </h3>
            {repo.rules.length ? (
              repo.rules.map(({ dir, rules }) => (
                <div key={dir || "/"} className={dir ? "dw-access-path" : "dw-access-root"}>
                  <code>{dir || "/"}</code> {formatRules(rules)}
                </div>
              ))
            ) : (
              <p className="dw-muted">no rules — instance default (signed-in: full, anon: per anon-read).</p>
            )}
          </div>
        ))}
      </SystemSection>
    </SystemPage>
  );
}

/** One subject row: name, optional description, optional member list (built-in
 *  `public`/`authed` have no membership; `admins` and named groups do). */
function GroupRow({
  name,
  desc,
  logins,
}: {
  name: string;
  desc?: string;
  logins?: string[];
}) {
  return (
    <div className="dw-access-group">
      <code className="dw-access-subject">{name}</code>
      {desc && <span className="dw-muted"> — {desc}</span>}
      {logins && (
        <ul className="dw-access-list">
          {logins.length ? (
            logins.map((l) => (
              <li key={l}>
                <code>{l}</code>
              </li>
            ))
          ) : (
            <li className="dw-muted">(none)</li>
          )}
        </ul>
      )}
    </div>
  );
}

/** `{authed=none, DBAs=full}` */
function formatRules(rules: Record<string, string>): string {
  const body = Object.entries(rules)
    .map(([subject, level]) => `${subject}=${level}`)
    .join(", ");
  return `{${body}}`;
}
